import { PageProps } from 'gatsby';
import { useEffect } from 'react';
import { Layout } from 'components/layout/Layout';
import { PublicLayout } from 'components/layout/PublicLayout';
import { ResultsLayout } from 'components/layout/ResultsLayout';
import { LoadingSpinner } from 'components/LoadingSpinner';
import { ServerError } from 'components/errorPages/ServerError';
import { AccessError } from 'components/errorPages/AccessError';
import { LicenseError } from 'components/errorPages/LicenseError';
import { RoleAccessDenied } from 'components/errorPages/RoleAccessDenied';
import { useTheme } from 'hooks/useTheme';
import { signIn } from 'services/authenticator';
import { useUser } from 'services/User';
import { useUserData } from 'services/api/UserData';
import { canAccessPath } from 'services/accessControl';

import '@opswat/ui/styles';
import '../../tailwind.css';
import 'components/WrapPage.css';

const PUBLIC_ROUTES = ['/licensing', '/extension/get-apikey'];

export const WrapPage = ({ children, ...props }: PageProps) => {
    const pathname = props.location.pathname;
    const isPublic = PUBLIC_ROUTES.includes(pathname);
    const { user } = useUser();
    const { error, isLoading, isProcessing } = useUserData({ shouldRetryOnError: false });
    useEffect(() => {
        if (isPublic === false && user?.isLoggedIn === false && !error && !isProcessing) {
            signIn();
        }
    }, [isPublic, user, error, isProcessing]);

    useTheme();

    if (isPublic) {
        return (
            <PublicLayout>
                {children}
            </PublicLayout>
        );
    }

    if (error?.response?.data?.code === 403004) {
        return (
            <PublicLayout>
                <AccessError />
            </PublicLayout>

        );
    }

    if (error?.response?.data?.code === 403002) {
        return (
            <PublicLayout>
                <AccessError link={false} />
            </PublicLayout>

        );
    }

    if (error?.message === 'Apikey is missing') {
        return (
            <PublicLayout>
                <LicenseError />
            </PublicLayout>
        );
    }
    
    if (error) {
        return (
            <PublicLayout>
                <ServerError />
            </PublicLayout>
        );
    }

    if (isLoading) {
        return (
            <LoadingSpinner />
        );
    }

    if (user?.licenseInfo?.paidUser !== 1) {
        return (
            <PublicLayout>
                <LicenseError />
            </PublicLayout>
        );
    }

    // Check role-based access control for protected routes
    if (user && !canAccessPath(user, pathname)) {
        return (
            <PublicLayout>
                <RoleAccessDenied />
            </PublicLayout>
        );
    }

    if (pathname.startsWith('/results')) {
        return (
            <ResultsLayout {...props}>
                {children}
            </ResultsLayout>
        );
    }

    return (
        <Layout {...props}>
            {children}
        </Layout>
    );
};
